import { useEffect, useState } from "react";
import clsx from "clsx";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ExternalLink,
  Images,
  MapPin,
  X,
} from "lucide-react";
import styles from "./EventGallery.module.scss";

const tilePattern = [
  "squareTile",
  "squareTile",
  "tallTile",
  "squareTile",
  "wideTile",
  "squareTile",
  "squareTile",
  "wideTile",
  "squareTile",
  "tallTile",
  "squareTile",
];

function getTileVariant(index) {
  if (index === 0) {
    return "heroTile";
  }

  return tilePattern[(index - 1) % tilePattern.length];
}

export default function EventGallery({ event, locale, t, fallbackDate }) {
  const [activeIndex, setActiveIndex] = useState(null);
  const activeImage = activeIndex === null ? null : event.images[activeIndex];

  const showPrevious = () => {
    setActiveIndex((current) =>
      current === null
        ? 0
        : (current - 1 + event.images.length) % event.images.length
    );
  };

  const showNext = () => {
    setActiveIndex((current) =>
      current === null ? 0 : (current + 1) % event.images.length
    );
  };

  useEffect(() => {
    if (activeIndex === null) {
      return undefined;
    }

    const onKeyDown = (eventKey) => {
      if (eventKey.key === "Escape") {
        setActiveIndex(null);
      }

      if (eventKey.key === "ArrowLeft") {
        setActiveIndex((current) =>
          current === null
            ? 0
            : (current - 1 + event.images.length) % event.images.length
        );
      }

      if (eventKey.key === "ArrowRight") {
        setActiveIndex((current) =>
          current === null ? 0 : (current + 1) % event.images.length
        );
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeIndex, event.images.length]);

  return (
    <>
      <article id={event.slug} className={styles.eventGallery}>
        <div className={styles.heroLayout}>
          <button
            type="button"
            className={styles.coverButton}
            onClick={() => setActiveIndex(0)}
          >
            <img
              src={event.images[0]}
              alt={`${event.title[locale]} 1`}
              loading="lazy"
            />
            <span className={styles.coverBadge}>{t("gallery_view_full")}</span>
          </button>

          <div className={styles.summaryCard}>
            <div className={styles.eyebrowRow}>
              <span>{fallbackDate}</span>
            </div>

            <div className={styles.heading}>
              <h2>{event.title[locale]}</h2>
              {<p>{event.description[locale]}</p>}
            </div>

            <div className={styles.metaGrid}>
              <div className={styles.metaItem}>
                <CalendarDays size={16} />
                <span>{fallbackDate}</span>
              </div>

              {event.time && (
                <div className={styles.metaItem}>
                  <Clock3 size={16} />
                  <span>
                    {t("gallery_time_label")}: {event.time}
                  </span>
                </div>
              )}

              {event.location?.[locale] && (
                <div className={styles.metaItem}>
                  <MapPin size={16} />
                  <span>
                    {t("gallery_venue_label")}: {event.location[locale]}
                  </span>
                </div>
              )}

              <div className={styles.metaItem}>
                <Images size={16} />
                <span>
                  {t("gallery_photos_label")}: {event.images.length}
                </span>
              </div>
            </div>

            <div className={styles.actions}>
              {event.eventLink && (
                <a
                  href={event.eventLink}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.primaryAction}
                >
                  <span>{t("gallery_event_link")}</span>
                  <ExternalLink size={16} />
                </a>
              )}

              <button
                type="button"
                className={styles.secondaryAction}
                onClick={() => setActiveIndex(0)}
              >
                <span>{t("gallery_view_full")}</span>
                <Images size={16} />
              </button>
            </div>
          </div>
        </div>

        <div className={styles.tiles}>
          {event.images.map((imagePath, index) => (
            <button
              key={imagePath}
              type="button"
              className={clsx(styles.tile, styles[getTileVariant(index)])}
              onClick={() => setActiveIndex(index)}
            >
              <img
                src={imagePath}
                alt={`${event.title[locale]} ${index + 1}`}
                loading="lazy"
              />
              <span className={styles.tileBadge}>
                {String(index + 1).padStart(2, "0")}
              </span>
            </button>
          ))}
        </div>
      </article>

      {activeImage && (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-modal="true"
          aria-label={event.title[locale]}
        >
          <button
            type="button"
            className={styles.lightboxBackdrop}
            onClick={() => setActiveIndex(null)}
            aria-label={t("gallery_close")}
          />

          <div className={styles.lightboxFrame}>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => setActiveIndex(null)}
              aria-label={t("gallery_close")}
            >
              <X size={22} />
            </button>

            <button
              type="button"
              className={clsx(styles.navButton, styles.leftNav)}
              onClick={showPrevious}
              aria-label={t("gallery_previous")}
            >
              <ChevronLeft size={26} />
            </button>

            <img
              src={activeImage}
              alt={`${event.title[locale]} ${activeIndex + 1}`}
              className={styles.lightboxImage}
            />

            <button
              type="button"
              className={clsx(styles.navButton, styles.rightNav)}
              onClick={showNext}
              aria-label={t("gallery_next")}
            >
              <ChevronRight size={26} />
            </button>

            <div className={styles.lightboxMeta}>
              <strong>{event.title[locale]}</strong>
              <span>
                {activeIndex + 1} / {event.images.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
